import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useLazyGetShipmentContainersListQuery,
  useLazyGetSingleShipmentContainerMediaQuery,
  useLazyGetShipmentContainerMediaByPhotoTypeQuery,
} from "@/src/store/apis/shipments/shipmentDetail";
import {
  useContainerIdsFullBatchDownloadMutation,
  useShipmentIdWithMediaTypeFullBatchDownloadMutation,
} from "@/src/store/apis/containerImage";
import { ContainerMediaType } from "@/src/store/apis/tasks/taskDetail/interface";
import { CONTAINER_VALUE_TYPE_CONTAINER_LABEL_TYPE_MAP } from "@/src/pages/exporter/Shipment/components/dialog/imageMapper";
import {
  ContainerPhotosAdapter,
  ContainerListItem,
  ContainerMediaData,
  MediaByPhotoType,
} from "../types";
import {
  mapContainerMediaResponse,
  mapMediaByPhotoTypeResponse,
} from "./utils/mapContainerMediaResponse";

/**
 * Exporter Shipment 모드용 Adapter
 * - 수출자가 선적 상세 페이지에서 컨테이너 사진을 조회할 때 사용
 * - shipmentId를 기반으로 해당 선적에 속한 모든 컨테이너 조회
 * - 인증된 수출자 사용자만 접근 가능
 *
 * @param shipmentId - 조회할 선적의 ID
 * @returns ContainerPhotosAdapter - 통합 어댑터 인터페이스
 *
 * @example
 * const adapter = useExporterShipmentAdapter(shipmentId);
 * await adapter.fetchContainerList();
 * await adapter.fetchContainerMedia(containerId);
 */
export const useExporterShipmentAdapter = (
  shipmentId: number
): ContainerPhotosAdapter => {
  const { t } = useTranslation();

  const [containerList, setContainerList] = useState<ContainerListItem[]>();
  const [containerMedia, setContainerMedia] = useState<ContainerMediaData>();
  const [mediaByPhotoType, setMediaByPhotoType] = useState<MediaByPhotoType>();

  const [getContainerList, containerListResult] =
    useLazyGetShipmentContainersListQuery();
  const [getSingleContainerMedia, singleContainerMediaResult] =
    useLazyGetSingleShipmentContainerMediaQuery();
  const [getMediaByPhotoType, mediaByPhotoTypeResult] =
    useLazyGetShipmentContainerMediaByPhotoTypeQuery();

  const [downloadByContainers, containerDownloadResult] =
    useContainerIdsFullBatchDownloadMutation();
  const [downloadByMediaTypes, photoTypeDownloadResult] =
    useShipmentIdWithMediaTypeFullBatchDownloadMutation();

  const fetchContainerList = useCallback(async () => {
    if (!shipmentId) {
      return undefined;
    }

    try {
      const response = await getContainerList({ id: shipmentId }).unwrap();
      const mapped: ContainerListItem[] =
        response?.map((item) => ({
          id: item.id,
          containerNo: item.containerNo ?? null,
        })) ?? [];

      setContainerList(mapped);
      return mapped;
    } catch (error) {
      console.error("[useExporterShipmentAdapter] fetchContainerList error:", error);
      return undefined;
    }
  }, [shipmentId, getContainerList]);

  const fetchContainerMedia = useCallback(
    async (containerId: number) => {
      try {
        const response = await getSingleContainerMedia({ id: containerId, shipmentId }).unwrap();
        if (response) {
          const mapped = mapContainerMediaResponse(response);
          setContainerMedia(mapped);
        }
      } catch (error) {
        console.error("[useExporterShipmentAdapter] fetchContainerMedia error:", error);
      }
    },
    [getSingleContainerMedia, shipmentId]
  );

  const fetchMediaByPhotoType = useCallback(async () => {
    if (!shipmentId) return;

    try {
      const response = await getMediaByPhotoType({ id: shipmentId }).unwrap();
      if (response) {
        const mapped = mapMediaByPhotoTypeResponse(response);
        setMediaByPhotoType(mapped);
      }
    } catch (error) {
      console.error("[useExporterShipmentAdapter] fetchMediaByPhotoType error:", error);
    }
  }, [shipmentId, getMediaByPhotoType]);

  const downloadByContainerIds = useCallback(
    async (ids: number[]) => {
      if (ids.length === 0) return;
      try {
        await downloadByContainers({ containerIdList: ids }).unwrap();
      } catch (error) {
        console.error("[useExporterShipmentAdapter] downloadByContainerIds error:", error);
      }
    },
    [downloadByContainers]
  );

  const downloadByPhotoTypes = useCallback(
    async (types: ContainerMediaType[]) => {
      if (types.length === 0 || !shipmentId) return;

      const photoTypeRequestIds = types
        .filter((type) => type.startsWith("CUSTOM_"))
        .map((type) => parseInt(type.replace("CUSTOM_", ""), 10))
        .filter((id) => !isNaN(id));

      const normalizedTypes = types
        .map((type) => (type.startsWith("CUSTOM_") ? "CUSTOM" : type))
        .filter((type, index, self) => self.indexOf(type) === index) as ContainerMediaType[];

      try {
        await downloadByMediaTypes({
          id: shipmentId,
          containerMediaTypeList: normalizedTypes,
          photoTypeRequestIds: photoTypeRequestIds.length > 0 ? photoTypeRequestIds : undefined,
        }).unwrap();
      } catch (error) {
        console.error("[useExporterShipmentAdapter] downloadByPhotoTypes error:", error);
      }
    },
    [shipmentId, downloadByMediaTypes]
  );

  const getPhotoTypeLabel = useCallback(
    (type: ContainerMediaType): string => {
      const labelMap = CONTAINER_VALUE_TYPE_CONTAINER_LABEL_TYPE_MAP(t);
      return labelMap[type] || type;
    },
    [t]
  );

  return {
    containerList,
    containerMedia,
    mediaByPhotoType,

    isContainerListLoading: containerListResult.isFetching,
    isContainerMediaLoading: singleContainerMediaResult.isFetching,
    isMediaByPhotoTypeLoading: mediaByPhotoTypeResult.isFetching,

    fetchContainerList,
    fetchContainerMedia,
    fetchMediaByPhotoType,

    downloadByContainerIds,
    downloadByPhotoTypes,
    isContainerDownloading: containerDownloadResult.isLoading,
    isPhotoTypeDownloading: photoTypeDownloadResult.isLoading,

    getPhotoTypeLabel,
  };
};
